import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Card } from '@/common/ui/Card/Card'
import { ScoreDistributionChart } from '@/common/ui/ScoreDistributionChart'
import { Button } from '@/common/ui/Form/Button/Button'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import {
    useCompanyStatsCommon,
    useCompanyStatsProducts,
    useCompanyStatsProductsScores,
    useCompanyStatsScores,
    useCompanyStatsSegmentScores,
} from '../model'
import type { CompanyStatsDataHooks } from '../model/hooks'
import { CompanyStatsSummaryCards } from './CompanyStatsSummaryCards'
import { CompanyStatsContentBlocks } from './CompanyStatsContentBlocks'
import { CompanyStatsCriteriaGrid } from './CompanyStatsCriteriaGrid'
import { CompanyStatsProductsRanking } from './CompanyStatsProductsRanking'
import { CompanyStatsSegmentComparison } from './CompanyStatsSegmentComparison'
import { CompanyStatsAllProductsTable } from './CompanyStatsAllProductsTable'

const defaultHooks: CompanyStatsDataHooks = {
    useCompanyStatsCommon,
    useCompanyStatsScores,
    useCompanyStatsSegmentScores,
    useCompanyStatsProducts,
    useCompanyStatsProductsScores,
}

type CompanyStatsDashboardProps = {
    orgId: string
    brandId?: string
    sellerId?: string
    hooks?: CompanyStatsDataHooks
    header?: ReactNode
    errorHeader?: ReactNode
}

export const CompanyStatsDashboard = ({
    orgId,
    brandId,
    sellerId,
    hooks = defaultHooks,
    header,
    errorHeader,
}: CompanyStatsDashboardProps) => {
    const toast = useOptionalToast()
    const lastErrorMessageRef = useRef<string | null>(null)
    const statsFilters = useMemo(() => ({ brand: brandId, seller: sellerId }), [brandId, sellerId])

    const {
        data: common,
        isLoading: isCommonLoading,
        error: commonError,
        refetch: refetchCommon,
    } = hooks.useCompanyStatsCommon(orgId, statsFilters)
    const {
        data: scores,
        isLoading: isScoresLoading,
        error: scoresError,
        refetch: refetchScores,
    } = hooks.useCompanyStatsScores(orgId, statsFilters)
    const {
        data: products,
        isLoading: isProductsLoading,
        error: productsError,
        refetch: refetchProducts,
    } = hooks.useCompanyStatsProducts(orgId, statsFilters)

    const chartData = useMemo(() => common?.scores ?? [], [common?.scores])
    const isLoading = isCommonLoading || isScoresLoading || isProductsLoading
    const error = commonError ?? scoresError ?? productsError

    const handleRetry = () => {
        void refetchCommon()
        void refetchScores()
        void refetchProducts()
    }

    useEffect(() => {
        if (!error) {
            lastErrorMessageRef.current = null
            return
        }

        if (lastErrorMessageRef.current === error.message) {
            return
        }

        lastErrorMessageRef.current = error.message
        toast?.showToast({
            type: 'error',
            message: `Ошибка загрузки: ${error.message}`,
        })
    }, [error, toast])

    if (isLoading) {
        return (
            <div className='flex items-center justify-center py-16'>
                <span className='loading loading-spinner loading-lg' aria-label='Загрузка статистики' />
            </div>
        )
    }

    if (error) {
        return (
            <div className='flex flex-col gap-4'>
                {errorHeader}
                <Button type='button' emphasis='accent' onClick={handleRetry}>
                    Обновить страницу
                </Button>
            </div>
        )
    }

    if (!common || !scores) {
        return null
    }

    return (
        <div className='flex min-w-0 flex-col gap-6'>
            {header}

            <CompanyStatsSummaryCards data={common} />

            <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
                <Card color='primary'>
                    <ScoreDistributionChart data={chartData} totalProducts={common.total_products} exportPngDisabled />
                </Card>
                <CompanyStatsContentBlocks scores={scores} />
            </div>

            <CompanyStatsCriteriaGrid blocks={scores.blocks} />

            {products ? <CompanyStatsProductsRanking data={products} /> : null}

            <CompanyStatsSegmentComparison orgId={orgId} brandId={brandId} sellerId={sellerId} hooks={hooks} />

            <CompanyStatsAllProductsTable orgId={orgId} brandId={brandId} sellerId={sellerId} hooks={hooks} />
        </div>
    )
}
